import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { AuthService } from './auth.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class TagService {
  private apiUrl = environment.api.url;
  token: string;

  constructor(private http: HttpClient, private authService: AuthService) {
  }

  // Get all tags
  getTags(): Observable<{ tags: any[] }> {
    const url = `${this.apiUrl}/tags/all`;

    return this.authService.token().pipe(
      mergeMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`, // Include the token in the request
          }),
        };

        return this.http.get<any>(url, httpOptions).pipe(
          map((response) => {
            if (!response || !response.data || !response.data.tags) {
              console.error('Failed to load tags: No data received', response);
              throw new Error('Failed to load tags: No data received');
            }

            // We expect the tags array to be an array of objects, so we map it as any[]
            return {tags: response.data.tags as any[]}; // Cast the tags to 'any[]'
          }),
          catchError((error) => {
            console.error('Error during fetching tags:', error);
            return throwError(
              () => new Error(`Failed to load tags: ${error.message}`)
            );
          })
        );
      })
    );
  }

  // Add a new custom tag
  addTag(name: string): Observable<{ tag: string }> {
    const url = `${this.apiUrl}/tags/add`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(url, {name}, httpOptions).pipe(
      map((response) => {
        if (!response || !response.data) {
          console.error('Failed to add tag: No data received', response);
          throw new Error('Failed to add tag: No data received');
        }
        return {tag: response.data};
      }),
      catchError((error) => {
        console.error('Error during adding tag:', error);
        return throwError(
          () => new Error(`Failed to add tag: ${error.message}`)
        );
      })
    );
  }

  // Remove a custom tag
  removeTag(name: string): Observable<{ tag: string }> {
    const url = `${this.apiUrl}/tags/remove`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http.post<any>(url, {name}, httpOptions).pipe(
      map((response) => {
        if (!response || !response.data) {
          console.error('Failed to remove tag: No data received', response);
          throw new Error('Failed to remove tag: No data received');
        }
        return {tag: response.data};
      }),
      catchError((error) => {
        console.error('Error during removing tag:', error);
        return throwError(
          () => new Error(`Failed to remove tag: ${error.message}`)
        );
      })
    );
  }

  // Attach a tag to a report
  attachTag(searchId: number, tags: any): Observable<void> {
    const url = `${this.apiUrl}/tags/attach`;

    this.authService.token().subscribe((token) => {
      this.token = token;
    });

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.token}`,
      }),
    };

    return this.http
      .post<any>(url, {search_id: searchId, tags}, httpOptions)
      .pipe(
        map((response) => {
          if (!response || !response.data) {
            console.error(
              'Failed to attach tag: Operation unsuccessful',
              response
            );
            throw new Error('Failed to attach tag: Operation unsuccessful');
          }
          return;
        }),
        catchError((error) => {
          console.error('Error during attaching tag:', error);
          return throwError(
            () => new Error(`Failed to attach tag: ${error.message}`)
          );
        })
      );
  }

  // Detach a tag from a report
  detachTag(
    tag: string,
    tagValues: string[],
    searchId: string
  ): Observable<void> {
    const url = `${this.apiUrl}/tags/detach`;

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
      }),
    };

    return this.http
      .post<any>(
        url,
        {tag, tag_values: tagValues, search_id: searchId},
        httpOptions
      )
      .pipe(
        map((response) => {
          if (!response || !response.success) {
            console.error(
              'Failed to detach tag: Operation unsuccessful',
              response
            );
            throw new Error('Failed to detach tag: Operation unsuccessful');
          }
          return;
        }),
        catchError((error) => {
          console.error('Error during detaching tag:', error);
          return throwError(
            () => new Error(`Failed to detach tag: ${error.message}`)
          );
        })
      );
  }

  getInvestmentBySearchId(searchId: number): Observable<any> {
    const url = `${this.apiUrl}/search/${searchId}/investment`;

    return this.authService.token().pipe(
      mergeMap((token) => {
        const httpOptions = {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          })
        };

        return this.http.get<any>(url, httpOptions).pipe(
          map((response) => {
            if (!response || !response.data) {
              console.error('Failed to load investment: No data received', response);
              throw new Error('Failed to load investment: No data received');
            }
            return response;
          }),
          catchError((error) => {
            console.error('Error during fetching investment:', error);
            return throwError(
              () => new Error(`Failed to load investment: ${error.message}`)
            );
          })
        );
      })
    );
  }

}
